import { useMemo } from "react";

import {
  ArrowPathIcon,
  BoltIcon,
  CloudIcon,
  CubeIcon,
  GlobeEuropeAfricaIcon,
  ScaleIcon,
  SparklesIcon,
  SunIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import Card from "../shared/Card/Card";
import classNames from "../../utils/classNames";
import { IVehicle } from "../../models/Vehicle";

/** Tiny deterministic 0..1 PRNG seeded from a string (FNV-1a + mulberry32). */
function seededRand(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const between = (rand: () => number, min: number, max: number) =>
  Math.round(min + rand() * (max - min));

const betweenF = (
  rand: () => number,
  min: number,
  max: number,
  decimals = 1
) => {
  const v = min + rand() * (max - min);
  const m = Math.pow(10, decimals);
  return Math.round(v * m) / m;
};

interface IVehicleSustainability {
  /** Total CO2e avoided vs producing a comparable new vehicle (kg). */
  co2SavedKg: number;
  /** Kerb weight assumption used for downstream calculations (kg). */
  vehicleWeightKg: number;
  /** Share of vehicle mass diverted from landfill (0..1). */
  landfillDiversionRate: number;
  /** Litres of fluids captured for safe disposal / re-refining. */
  fluidsCapturedLitres: number;
  /** Energy saved through material recovery (kWh). */
  energySavedKwh: number;
  /** Material recovery breakdown — values in kg, must sum close to vehicleWeightKg. */
  materials: {
    ferrousMetal: number;
    nonFerrousMetal: number;
    plastics: number;
    glass: number;
    rubber: number;
    fluids: number;
    residual: number;
  };
  /** Reuse vs recycle split of everything recovered (0..1). */
  reuseShare: number;
  recycleShare: number;
  recoverShare: number;
}

const buildMockVehicleSustainability = (
  vehicle: IVehicle
): IVehicleSustainability => {
  const seed = vehicle.id || vehicle.vrm || "demo-vehicle";
  const rand = seededRand(seed);

  const weightKg =
    vehicle.grossWeightKG ?? between(rand, 1100, 1950);

  // CO2 from manufacturing a new car is ~6–7 tCO2e; dismantling + reuse
  // captures the embodied carbon of the parts we keep in service. Use a
  // deterministic fraction of that embodied carbon.
  const co2SavedKg = Math.round(
    weightKg * betweenF(rand, 1.6, 3.2, 2) // 1.6..3.2 kg CO2e per kg recovered
  );

  // UK / EU ELV directive target is 95% recovery, 85% reuse/recycle.
  const landfillDiversionRate = betweenF(rand, 0.9, 0.98, 3);

  const fluidsCapturedLitres = between(rand, 35, 70);
  const energySavedKwh = Math.round(weightKg * betweenF(rand, 2.8, 4.6, 2));

  // Material composition of a typical passenger vehicle with a little jitter.
  const ferrousShare = betweenF(rand, 0.58, 0.68, 3);
  const nonFerrousShare = betweenF(rand, 0.07, 0.11, 3);
  const plasticShare = betweenF(rand, 0.08, 0.12, 3);
  const glassShare = betweenF(rand, 0.02, 0.04, 3);
  const rubberShare = betweenF(rand, 0.03, 0.05, 3);
  const fluidsShare = betweenF(rand, 0.02, 0.035, 3);
  const residualShare = Math.max(
    0.015,
    1 -
      ferrousShare -
      nonFerrousShare -
      plasticShare -
      glassShare -
      rubberShare -
      fluidsShare
  );

  const materials = {
    ferrousMetal: Math.round(weightKg * ferrousShare),
    nonFerrousMetal: Math.round(weightKg * nonFerrousShare),
    plastics: Math.round(weightKg * plasticShare),
    glass: Math.round(weightKg * glassShare),
    rubber: Math.round(weightKg * rubberShare),
    fluids: Math.round(weightKg * fluidsShare),
    residual: Math.round(weightKg * residualShare),
  };

  const reuseShare = betweenF(rand, 0.22, 0.38, 3);
  const recycleShare = betweenF(rand, 0.5, 0.62, 3);
  const recoverShare = Math.max(0, 1 - reuseShare - recycleShare);

  return {
    co2SavedKg,
    vehicleWeightKg: weightKg,
    landfillDiversionRate,
    fluidsCapturedLitres,
    energySavedKwh,
    materials,
    reuseShare,
    recycleShare,
    recoverShare,
  };
};

const formatKg = (n: number) =>
  n >= 1000
    ? `${(n / 1000).toLocaleString("en-GB", { maximumFractionDigits: 2 })} t`
    : `${n.toLocaleString("en-GB")} kg`;

const formatPct = (n: number) => `${Math.round(n * 100)}%`;

const formatInt = (n: number) => n.toLocaleString("en-GB");

export default function VehicleSustainability({
  vehicle,
}: {
  vehicle: IVehicle;
}) {
  const data = useMemo(() => buildMockVehicleSustainability(vehicle), [vehicle]);

  // Relatable comparisons so the headline figure lands without needing to
  // know what "kg CO2e" means in the abstract.
  const treesEquivalent = Math.round(data.co2SavedKg / 22); // ~22 kg CO2/yr per tree
  const kmEquivalent = Math.round(data.co2SavedKg * 6.1); // ~164 g CO2 per passenger-km
  const householdYears = (data.energySavedKwh / 2900).toFixed(1); // 2900 kWh avg UK household / yr

  const totalMass = Object.values(data.materials).reduce((a, b) => a + b, 0);
  const materialRows: {
    label: string;
    mass: number;
    color: string;
    tone: string;
  }[] = [
    {
      label: "Ferrous metal",
      mass: data.materials.ferrousMetal,
      color: "bg-slate-500",
      tone: "text-slate-700",
    },
    {
      label: "Non-ferrous metal",
      mass: data.materials.nonFerrousMetal,
      color: "bg-amber-400",
      tone: "text-amber-700",
    },
    {
      label: "Plastics",
      mass: data.materials.plastics,
      color: "bg-sky-500",
      tone: "text-sky-700",
    },
    {
      label: "Glass",
      mass: data.materials.glass,
      color: "bg-cyan-400",
      tone: "text-cyan-700",
    },
    {
      label: "Rubber",
      mass: data.materials.rubber,
      color: "bg-zinc-700",
      tone: "text-zinc-700",
    },
    {
      label: "Fluids",
      mass: data.materials.fluids,
      color: "bg-blue-500",
      tone: "text-blue-700",
    },
    {
      label: "Residual",
      mass: data.materials.residual,
      color: "bg-stone-400",
      tone: "text-stone-700",
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 p-5">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br from-emerald-200/50 to-sky-200/40 blur-2xl"
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 via-teal-500 to-sky-500 text-white shadow-sm">
              <GlobeEuropeAfricaIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900">
                  Vehicle Sustainability
                </h2>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-inset ring-amber-200">
                  Demo data
                </span>
              </div>
              <p className="max-w-xl text-xs text-gray-600">
                Estimated environmental impact from dismantling, reusing and
                recycling this vehicle rather than sending it to landfill.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-start sm:items-end">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
              CO₂ avoided
            </p>
            <p className="text-3xl font-bold text-emerald-700">
              {formatKg(data.co2SavedKg)}
            </p>
            <p className="text-[11px] text-emerald-800/80">
              ≈ {formatInt(treesEquivalent)} trees · 1&nbsp;yr of absorption
            </p>
          </div>
        </div>
      </div>

      {/* Headline stats row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <HeadlineStat
          icon={ScaleIcon}
          label="Diverted from landfill"
          value={formatPct(data.landfillDiversionRate)}
          hint={`${formatKg(Math.round(data.vehicleWeightKg * data.landfillDiversionRate))} recovered`}
          tone="emerald"
        />
        <HeadlineStat
          icon={BoltIcon}
          label="Energy saved"
          value={`${formatInt(data.energySavedKwh)} kWh`}
          hint={`≈ ${householdYears} UK households · 1 yr`}
          tone="amber"
        />
        <HeadlineStat
          icon={CloudIcon}
          label="Equivalent car km"
          value={formatInt(kmEquivalent)}
          hint="Avoided tail-pipe CO₂"
          tone="sky"
        />
        <HeadlineStat
          icon={TruckIcon}
          label="Fluids captured"
          value={`${formatInt(data.fluidsCapturedLitres)} L`}
          hint="Oil, coolant, fuel, refrigerant"
          tone="teal"
        />
      </div>

      {/* Recovery pathway */}
      <Card title="Recovery pathway" bodyClassName="p-4">
        <p className="text-xs text-gray-500 mb-3">
          How the recovered mass flows through dismantling. Reuse keeps
          components in service; recycle feeds material back into production;
          recover covers energy-from-waste routes.
        </p>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="bg-emerald-500"
            style={{ width: `${data.reuseShare * 100}%` }}
          />
          <div
            className="bg-sky-500"
            style={{ width: `${data.recycleShare * 100}%` }}
          />
          <div
            className="bg-amber-400"
            style={{ width: `${data.recoverShare * 100}%` }}
          />
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
          <PathwayTile
            icon={ArrowPathIcon}
            label="Reuse"
            value={formatPct(data.reuseShare)}
            description="Tested parts resold for direct fit."
            tone="emerald"
          />
          <PathwayTile
            icon={CubeIcon}
            label="Recycle"
            value={formatPct(data.recycleShare)}
            description="Material recovered for re-manufacture."
            tone="sky"
          />
          <PathwayTile
            icon={SunIcon}
            label="Recover"
            value={formatPct(data.recoverShare)}
            description="Residual sent to energy recovery."
            tone="amber"
          />
        </div>
      </Card>

      {/* Material breakdown */}
      <Card
        title="Material breakdown"
        bodyClassName="p-4"
        headerRight={
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
            <ScaleIcon className="h-3 w-3" aria-hidden="true" />
            {formatKg(data.vehicleWeightKg)} kerb weight
          </span>
        }
      >
        <div className="flex flex-col gap-2">
          {materialRows.map((row) => {
            const pct = totalMass > 0 ? (row.mass / totalMass) * 100 : 0;
            return (
              <div key={row.label}>
                <div className="flex items-center justify-between text-xs">
                  <span className={classNames("font-medium", row.tone)}>
                    {row.label}
                  </span>
                  <span className="tabular-nums text-gray-600">
                    {formatKg(row.mass)}
                    <span className="ml-1 text-gray-400">
                      ({pct.toFixed(1)}%)
                    </span>
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className={classNames("h-full", row.color)}
                    style={{ width: `${pct}%` }}
                    aria-hidden="true"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Narrative panel */}
      <Card bodyClassName="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100">
            <SparklesIcon className="h-4 w-4" aria-hidden="true" />
          </div>
          <p className="text-sm leading-relaxed text-gray-700">
            Dismantling this{" "}
            <span className="font-semibold text-gray-900">
              {[vehicle.dvlaYearOfManufacture, vehicle.combinedMake, vehicle.combinedModel]
                .filter(Boolean)
                .join(" ") || "vehicle"}
            </span>{" "}
            kept{" "}
            <span className="font-semibold text-emerald-700">
              {formatKg(
                Math.round(data.vehicleWeightKg * data.landfillDiversionRate)
              )}
            </span>{" "}
            of material out of landfill and avoided roughly{" "}
            <span className="font-semibold text-emerald-700">
              {formatKg(data.co2SavedKg)} of CO₂e
            </span>{" "}
            — the equivalent of{" "}
            <span className="font-semibold">
              {formatInt(treesEquivalent)} trees
            </span>{" "}
            absorbing carbon for a year, or{" "}
            <span className="font-semibold">
              {formatInt(kmEquivalent)} km
            </span>{" "}
            of average-car driving.
          </p>
        </div>
      </Card>
    </div>
  );
}

function HeadlineStat({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  value: string;
  hint?: string;
  tone: "emerald" | "sky" | "amber" | "teal";
}) {
  const toneMap = {
    emerald: {
      ring: "ring-emerald-200",
      bg: "bg-emerald-50",
      icon: "text-emerald-600",
      value: "text-emerald-700",
    },
    sky: {
      ring: "ring-sky-200",
      bg: "bg-sky-50",
      icon: "text-sky-600",
      value: "text-sky-700",
    },
    amber: {
      ring: "ring-amber-200",
      bg: "bg-amber-50",
      icon: "text-amber-600",
      value: "text-amber-700",
    },
    teal: {
      ring: "ring-teal-200",
      bg: "bg-teal-50",
      icon: "text-teal-600",
      value: "text-teal-700",
    },
  }[tone];
  return (
    <div
      className={classNames(
        "flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 ring-1 ring-inset",
        toneMap.ring
      )}
    >
      <div
        className={classNames(
          "flex h-9 w-9 flex-none items-center justify-center rounded-md",
          toneMap.bg,
          toneMap.icon
        )}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <p
          className={classNames(
            "truncate text-lg font-bold leading-tight",
            toneMap.value
          )}
        >
          {value}
        </p>
        {hint && (
          <p className="mt-0.5 truncate text-[11px] text-gray-500">{hint}</p>
        )}
      </div>
    </div>
  );
}

function PathwayTile({
  icon: Icon,
  label,
  value,
  description,
  tone,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  value: string;
  description: string;
  tone: "emerald" | "sky" | "amber";
}) {
  const toneMap = {
    emerald: {
      bg: "bg-emerald-50",
      ring: "ring-emerald-100",
      icon: "text-emerald-600",
      value: "text-emerald-700",
    },
    sky: {
      bg: "bg-sky-50",
      ring: "ring-sky-100",
      icon: "text-sky-600",
      value: "text-sky-700",
    },
    amber: {
      bg: "bg-amber-50",
      ring: "ring-amber-100",
      icon: "text-amber-600",
      value: "text-amber-700",
    },
  }[tone];
  return (
    <div
      className={classNames(
        "rounded-md px-3 py-2 ring-1 ring-inset",
        toneMap.bg,
        toneMap.ring
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className={classNames("h-4 w-4", toneMap.icon)} aria-hidden="true" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </span>
      </div>
      <p className={classNames("mt-1 text-xl font-bold", toneMap.value)}>
        {value}
      </p>
      <p className="mt-0.5 text-[11px] leading-snug text-gray-600">
        {description}
      </p>
    </div>
  );
}
